<template>
  <div>
    <div v-if="positionOptions.length > 1" class="mb-6">
      <Tab
        :tabs="positionTabLabels"
        border
        :active-tab="activePositionTab"
        @currentTab="onPositionTabChange"
      />
    </div>

    <div v-if="loading">
      <loader size="xxs" :loader-image="false" />
    </div>
    <div v-else-if="showError">
      <ErrorPage />
    </div>
    <div v-else>
      <org-chart
        class="-mt-6"
        v-if="ds && ds.children && ds.children.length > 0"
        :datasource="ds"
      >
        <template slot-scope="{ nodeData }">
          <div class="w-full flex gap-3 org-node">
            <div class="flex">
              <img
                class="tw-mx-auto tw-rounded-full"
                style="height: 30px; width: 30px"
                :src="nodeData.photo"
                alt="account"
                v-if="nodeData.photo"
              />
              <div
                v-else
                class="w-8 h-8 rounded text-blueCrayola border text-center font-semibold pt-1"
              >
                {{ $getInitials(`${nodeData.name}`) }}
              </div>
            </div>
            <div class="flex flex-col gap-1">
              <div class="font-semibold flex text-base text-romanSilver">
                {{ nodeData.name }}
              </div>
              <div class="text-xs text-darkPurple capitalize text-left flex flex-col gap-1">
                <p class="font-semibold">
                  {{ nodeData.title }}
                </p>
                <p v-if="nodeData.level" class="font-semibold">
                  {{ nodeData.level }}
                </p>
              </div>
            </div>
          </div>
          <div
            class="w-full flex justify-center mb-3"
            v-if="nodeData.users && nodeData.users.length > 1"
          >
            <div class="w-[48px] py-2 px-3 -bottom-2 bg-darkPurple rounded-md absolute">
              <p class="text-white text-xs font-semibold">
                +{{ nodeData.users.length }}
              </p>
            </div>
          </div>
          <div
            v-else-if="nodeData.reportCount > 0"
            class="w-full flex justify-center mb-3"
          >
            <div class="w-[48px] py-2 px-3 -bottom-2 bg-darkPurple rounded-md absolute">
              <p class="text-white text-xs font-semibold">
                {{ nodeData.reportCount }}
              </p>
            </div>
          </div>
        </template>
      </org-chart>

      <div class="flex flex-col gap-1" v-else>
        <div class="flex w-full justify-center">
          <Icon
            icon-name="dashboard-error"
            class="-mt-1 iconSize"
            style="width: 250px; height: 250px"
          />
        </div>
        <p class="text-xl text-center font-bold">No Data Available</p>
        <p class="text-xs text-center text-romanSilver">
          There is no data to show currently.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Tab from "@scelloo/cloudenly-ui/src/components/tab";
import ErrorPage from "@/modules/Admin/error403";
import Icon from "@/components/Icon";
import OrgChart from "./OrganizationChartContainer";

const STANDARD_KEY = "standard";
const ADMINISTRATIVE_KEY = "administrative";

export default {
  name: "DirectReporting",
  components: { OrgChart, ErrorPage, Icon, Tab },
  data() {
    return {
      responseData: null,
      selectedPositionKey: STANDARD_KEY,
      loading: true,
      showError: false,
    };
  },
  props: {
    userDetails: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    positionOptions() {
      if (!this.responseData) return [];

      const options = [];
      if (this.responseData.designation?.name) {
        options.push({
          key: STANDARD_KEY,
          label: this.responseData.designation.name,
        });
      }
      if (this.responseData.administrativeDesignation?.name) {
        options.push({
          key: ADMINISTRATIVE_KEY,
          label: this.responseData.administrativeDesignation.name,
        });
      }
      return options;
    },
    positionTabLabels() {
      return this.positionOptions.map((option) => option.label);
    },
    activePositionTab() {
      const selected = this.positionOptions.find(
        (option) => option.key === this.selectedPositionKey
      );
      return selected?.label || this.positionTabLabels[0] || "";
    },
    ds() {
      if (!this.responseData) return null;
      return this.transformRoot(
        this.responseData,
        this.selectedPositionKey === ADMINISTRATIVE_KEY
      );
    },
  },
  methods: {
    onPositionTabChange(tabLabel) {
      const match = this.positionOptions.find((option) => option.label === tabLabel);
      if (match) {
        this.selectedPositionKey = match.key;
      }
    },
    getLevelLabel(node) {
      if (!node?.level) return "";
      const name =
        typeof node.level === "string" ? node.level : node.level.name;
      if (!name) return "";
      // Avoid "Level Level 4" when API already returns a Level-prefixed name
      if (/^level\b/i.test(String(name).trim())) {
        return String(name).trim();
      }
      return `Level ${name}`;
    },
    getDesignationTitle(node, useAdministrative = false) {
      if (useAdministrative && node.administrativeDesignation?.name) {
        return node.administrativeDesignation.name;
      }
      return node.designation?.name || node.userDesignation?.name || "";
    },
    getChildren(node, useAdministrative = false) {
      if (useAdministrative) {
        return Array.isArray(node.administrativeReportees)
          ? node.administrativeReportees
          : [];
      }
      return Array.isArray(node.reportees) ? node.reportees : [];
    },
    transformNode(node) {
      if (!node) return null;
      const children = this.getChildren(node, false);
      return {
        id: node.id,
        name: `${node.fname || ""} ${node.lname || ""}`.trim(),
        title: this.getDesignationTitle(node, false),
        level: this.getLevelLabel(node),
        photo: node.photo || null,
        users: node.otherDesignationUser || [],
        reportCount: children.length,
        children: children.map((reportee) => this.transformNode(reportee)),
      };
    },
    transformRoot(node, useAdministrative = false) {
      if (!node) return null;
      const children = this.getChildren(node, useAdministrative);
      return {
        id: node.id,
        name: `${node.fname || ""} ${node.lname || ""}`.trim(),
        title: this.getDesignationTitle(node, useAdministrative),
        level: this.getLevelLabel(node),
        photo: node.photo || null,
        users: node.otherDesignationUser || [],
        reportCount: children.length,
        children: children.map((reportee) => this.transformNode(reportee)),
      };
    },
  },
  async mounted() {
    try {
      if (!this.$route.query.slug) {
        await this.$handlePrivilege("employeeDirectory", "viewDirectReportED");
      }
      const response = await this.$_getEmployeeDirectReportInsights(
        this.userDetails.userId
      );
      this.responseData = response.data.data;

      if (this.responseData?.administrativeDesignation?.name) {
        const hasStandardReportees =
          Array.isArray(this.responseData.reportees) &&
          this.responseData.reportees.length > 0;
        const hasAdminReportees =
          Array.isArray(this.responseData.administrativeReportees) &&
          this.responseData.administrativeReportees.length > 0;

        if (!hasStandardReportees && hasAdminReportees) {
          this.selectedPositionKey = ADMINISTRATIVE_KEY;
        } else {
          this.selectedPositionKey = STANDARD_KEY;
        }
      }
    } catch (error) {
      this.showError = true;
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style>
.org-node::after {
  content: "";
  position: absolute;
  top: -104px;
  left: 50.2%;
  transform: translateX(-50%);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid #ccc;
}
</style>
